<template>
    <section class="documents-and-webpages-step">
        <DocumentPreview
            ref="documentPreview"
            :product="previewProduct"
            :file="previewDocument"
        />

        <ReviewWorkspaceHost
            :visible="reviewVisible"
            :process-id="selectedReviewProcessId"
            :profile="reviewProfile"
            :review-context="reviewContext"
            @close="closeReview"
            @completed="handleReviewCompleted"
        />

        <BModal
            v-model="candidatePreviewVisible"
            size="xl"
            centered
            scrollable
            ok-only
            :title="previewCandidate ? previewCandidate.filename : ''"
            :ok-title="translate('Component(Button):close', 'Close')"
            @hidden="cleanupCandidatePreview"
        >
            <div v-if="candidatePreviewLoading" class="text-center py-3">
                <BSpinner variant="primary" />
            </div>
            <BAlert v-else-if="candidatePreviewError" show variant="danger">
                <div class="alert-body">{{ candidatePreviewError }}</div>
            </BAlert>
            <template v-else-if="candidatePreviewUrl">
                <object
                    v-if="canInlineCandidatePreview"
                    :data="candidatePreviewUrl"
                    :type="candidatePreviewType"
                    class="documents-and-webpages-step__candidate-preview"
                >
                    {{ translate(
                        'Page(ReceivedRequest):Component(DocumentsAndWebpages):preview-unavailable',
                        'Preview is unavailable for this document.',
                    ) }}
                </object>
                <div v-else class="text-center py-3">
                    <p>
                        {{ translate(
                            'Page(ReceivedRequest):Component(DocumentsAndWebpages):preview-download-only',
                            'This file type cannot be previewed safely. Download it to inspect the document.',
                        ) }}
                    </p>
                    <BButton
                        variant="primary"
                        :href="candidatePreviewUrl"
                        :download="previewCandidate ? previewCandidate.filename : ''"
                    >
                        {{ translate('Component(Button):download', 'Download') }}
                    </BButton>
                </div>
            </template>
        </BModal>

        <RequestedVerificationDocuments
            :requested-documents="requestedVerificationDocuments"
            :requested-evidence-report-types="requestedEvidenceReportTypes"
            :provided-documents="providedVerificationDocuments"
            class="mb-1"
        />

        <BRow class="documents-and-webpages-step__panels">
            <BCol cols="12" md="6" class="d-flex mb-1 mb-md-0">
                <div class="documents-and-webpages-step__source-panel w-100">
                    <h5 class="mb-1">
                        {{ translate('Page(ReceivedRequest):Component(DocumentsAndWebpages):upload-title', 'Upload documents') }}
                    </h5>

                    <BAlert
                        v-if="selectionError"
                        variant="danger"
                        show
                        class="mb-1"
                    >
                        <div class="alert-body">
                            {{ selectionError }}
                        </div>
                    </BAlert>

                    <div
                        class="documents-and-webpages-step__drop-area"
                        :class="{ 'documents-and-webpages-step__drop-area--dragging': isDragging }"
                        @dragenter.prevent.stop="handleDragEnter"
                        @dragover.prevent.stop
                        @dragleave.prevent.stop="handleDragLeave"
                        @drop.prevent.stop="handleDrop"
                    >
                        <BFormFile
                            ref="fileInput"
                            v-model="selectedFiles"
                            :accept="acceptValue"
                            :multiple="settings.multipleFiles !== false"
                            :disabled="runtimeState.isSaving"
                            :placeholder="translate(
                                'Page(ReceivedRequest):Component(DocumentsAndWebpages):choose-files',
                                'Choose files',
                            )"
                            :drop-placeholder="translate(
                                'Page(ReceivedRequest):Component(DocumentsAndWebpages):drop-files',
                                'Drop files here',
                            )"
                            @input="handleSelectedFiles"
                        />

                        <p
                            class="documents-and-webpages-step__drop-prompt mb-50 mt-1 text-center"
                            role="button"
                            tabindex="0"
                            @click="openFilePicker"
                            @keydown.enter.prevent="openFilePicker"
                            @keydown.space.prevent="openFilePicker"
                        >
                            {{ translate(
                                'Page(ReceivedRequest):Component(DocumentsAndWebpages):choose-or-drop',
                                'Choose files or drop them here',
                            ) }}
                        </p>
                        <p class="mb-0 text-center text-less-muted small">
                            {{ supportedFormatsLabel }}
                        </p>
                    </div>

                    <div
                        v-if="queuedFiles.length"
                        class="documents-and-webpages-step__queue mt-1"
                    >
                        <BBadge
                            v-for="file in queuedFiles"
                            :key="fileKey(file)"
                            variant="light-secondary"
                            class="mr-50 mb-50"
                        >
                            {{ file.name }}
                        </BBadge>

                        <div class="text-right mt-50">
                            <BButton
                                variant="outline-secondary"
                                class="mr-1"
                                :disabled="runtimeState.isSaving"
                                @click="clearSelection"
                            >
                                {{ translate('Component(Button):cancel', 'Cancel') }}
                            </BButton>
                            <BButton
                                variant="primary"
                                :disabled="runtimeState.isSaving || Boolean(selectionError)"
                                @click="upload"
                            >
                                <BSpinner v-if="runtimeState.isSaving" small />
                                <span v-else>{{ translate('Component(Button):upload', 'Upload') }}</span>
                            </BButton>
                        </div>
                    </div>
                </div>
            </BCol>

            <BCol
                v-if="settings.showWebpageLinks !== false"
                cols="12"
                md="6"
                class="d-flex"
            >
                <div class="documents-and-webpages-step__source-panel w-100">
                    <h5 class="mb-1">
                        {{ translate(
                            'Page(ReceivedRequest):Component(DocumentsAndWebpages):add-webpage-title',
                            'Add a webpage',
                        ) }}
                    </h5>
                    <BFormGroup
                        :label="translate(
                            'Page(ReceivedRequest):Component(DocumentsAndWebpages):url-label',
                            'URL',
                        )"
                    >
                        <BFormInput
                            v-model="webpageUrl"
                            type="url"
                            maxlength="2048"
                            :disabled="runtimeState.isSaving"
                            :state="webpageError ? false : null"
                            placeholder="https://..."
                            @input="handleWebpageInput"
                            @keydown.enter.prevent="addWebpage"
                        />
                        <small v-if="webpageError" class="text-danger">{{ webpageError }}</small>
                    </BFormGroup>
                    <div class="documents-and-webpages-step__link-actions">
                        <BButton
                            variant="primary"
                            :disabled="runtimeState.isSaving || !webpageUrl.trim()"
                            @click="addWebpage"
                        >
                            <BSpinner v-if="linkSavePending && runtimeState.isSaving" small class="mr-50" />
                            {{ translate('Component(Button):add-link', 'Add link') }}
                        </BButton>
                        <div class="documents-and-webpages-step__link-processing-note">
                            <FeatherIcon icon="InfoIcon" size="16" aria-hidden="true" />
                            <small>
                                {{ translate(
                                    'Page(ReceivedRequest):Component(DocumentsAndWebpages):document-research-note',
                                    'Eligible REACH and RoHS links are searched for documents automatically.',
                                ) }}
                            </small>
                        </div>
                    </div>
                </div>
            </BCol>
        </BRow>

        <div
            v-if="documents.length || documentationLinks.length"
            class="documents-and-webpages-step__sources mt-2"
        >
            <h5 class="mb-1">
                {{ translate('Page(ReceivedRequest):Component(DocumentsAndWebpages):added-sources', 'Added sources') }}
            </h5>

            <BAlert
                v-if="hasDocumentsAwaitingReview"
                show
                variant="warning"
                class="app-alert--attention mb-1"
                data-testid="document-review-required-notice"
            >
                <div class="alert-body d-flex align-items-center">
                    <FeatherIcon
                        icon="InfoIcon"
                        size="18"
                        class="mr-75 flex-shrink-0"
                        aria-hidden="true"
                    />
                    <span>
                        {{ translate(
                            'Page(ReceivedRequest):Component(DocumentsAndWebpages):review-required-notice',
                            'You need to review and approve the automatic interpretation of each document before its content is included in your response.',
                        ) }}
                    </span>
                </div>
            </BAlert>

            <div
                v-for="document in documents"
                :key="document.id"
                class="documents-and-webpages-step__source-row"
            >
                <span class="documents-and-webpages-step__file-icon" aria-hidden="true">
                    <FeatherIcon icon="FileTextIcon" size="20" />
                </span>

                <div class="documents-and-webpages-step__source-copy">
                    <span class="documents-and-webpages-step__file-name">
                        {{ documentName(document) }}
                    </span>
                    <small
                        v-if="failureReason(document)"
                        class="text-danger"
                    >
                        {{ failureReason(document) }}
                    </small>
                </div>

                <BBadge
                    v-if="!isReviewPending(document)"
                    :variant="statusVariant(document)"
                    class="documents-and-webpages-step__status"
                >
                    <ShimmerText :active="isAnalysisProcessing(document)">
                        {{ statusLabel(document) }}
                    </ShimmerText>
                </BBadge>

                <BButton
                    v-if="canPreviewDocument(document)"
                    variant="primary"
                    size="sm"
                    class="btn-icon documents-and-webpages-step__preview"
                    :title="translate('Component(Button):preview', 'Preview')"
                    :aria-label="translate('Component(Button):preview', 'Preview')"
                    @click="openDocumentPreview(document)"
                >
                    <FeatherIcon icon="EyeIcon" />
                </BButton>

                <BButton
                    v-if="isReviewPending(document)"
                    variant="primary"
                    :disabled="!reviewProcessId(document)
                        || reviewTranslationsLoading
                        || isReviewRefreshPending(document)"
                    @click="openReview(document)"
                >
                    <BSpinner
                        v-if="isReviewRefreshPending(document)"
                        small
                        class="mr-50"
                        data-testid="review-status-refresh"
                    />
                    {{ translate('Component(Button):review', 'Review') }}
                </BButton>
            </div>

            <DocumentResearchSource
                v-for="link in documentationLinks"
                :key="`link-${link.id}`"
                :link="link"
                :process="researchProcessFor(link)"
                :disabled="runtimeState.isSaving"
                :previewing-candidate-id="previewingCandidateId"
                @preview="previewResearchCandidate"
                @select="selectResearchCandidates"
                @finish-without-documents="finishResearchWithoutDocuments"
                @review="openResearchReview"
            />
        </div>
    </section>
</template>

<script>
import {
    BAlert,
    BBadge,
    BButton,
    BCol,
    BFormFile,
    BFormGroup,
    BFormInput,
    BModal,
    BRow,
    BSpinner,
} from 'bootstrap-vue'
import ReviewWorkspaceHost from '@/views/research/review/workspace/ReviewWorkspaceHost.vue'
import ShimmerText from '@core/components/common/ShimmerText.vue'
import DocumentPreview from '@core/components/product/DocumentPreview.vue'
import RequestedVerificationDocuments from '@/views/request/components/RequestedVerificationDocuments.vue'
import {
    DOCUMENT_REVIEW_TRANSLATION_GROUP,
    loadTranslationGroups,
} from '@/libs/i18n'
import DocumentResearchSource from './DocumentResearchSource.vue'
import {
    DocumentProcessingStatus,
    getDocumentProcessingStatus,
    getDocumentReviewProcessId,
    isDocumentAnalysisProcessing,
} from './document-processing'
import {
    blocksDocumentResearchCompletion,
    isActionRequiredDocumentResearchStatus,
    isActiveDocumentResearchStatus,
} from './document-research'

const STATUS_FALLBACKS = Object.freeze({
    [DocumentProcessingStatus.UPLOADED]: 'Uploaded',
    [DocumentProcessingStatus.CREATED]: 'Queued',
    [DocumentProcessingStatus.ANALYSIS_PENDING]: 'Processing',
    [DocumentProcessingStatus.ANALYSIS_COMPLETED]: 'Processing',
    [DocumentProcessingStatus.REVIEW_PENDING]: 'Ready for review',
    [DocumentProcessingStatus.COMPLETED]: 'Completed',
    [DocumentProcessingStatus.REJECTED]: 'Rejected',
    [DocumentProcessingStatus.FAILED]: 'Failed',
})

export default {
    name: 'DocumentsAndWebpagesStep',
    components: {
        BAlert,
        BBadge,
        BButton,
        BCol,
        BFormFile,
        BFormGroup,
        BFormInput,
        BModal,
        BRow,
        BSpinner,
        DocumentPreview,
        RequestedVerificationDocuments,
        ReviewWorkspaceHost,
        ShimmerText,
        DocumentResearchSource,
    },
    props: {
        context: { type: Object, required: true },
        settings: { type: Object, required: true },
        runtimeState: { type: Object, required: true },
    },
    data() {
        return {
            selectedFiles: [],
            queuedFiles: [],
            selectionError: null,
            uploadRevision: 0,
            pendingUploadRevision: null,
            dragDepth: 0,
            isDragging: false,
            reviewVisible: false,
            reviewTranslationsLoading: false,
            selectedReviewProcessId: null,
            selectedDocumentProcessId: null,
            selectedResearchProcessId: null,
            previewDocument: null,
            webpageUrl: '',
            webpageError: null,
            linkRevision: 0,
            pendingLinkRevision: null,
            candidatePreviewVisible: false,
            candidatePreviewLoading: false,
            candidatePreviewError: null,
            candidatePreviewUrl: '',
            candidatePreviewType: '',
            previewCandidate: null,
            previewingCandidateId: null,
            candidatePreviewRevision: 0,
        }
    },
    computed: {
        requestedVerificationDocuments() {
            return this.context.request && Array.isArray(this.context.request.verificationDocuments)
                ? this.context.request.verificationDocuments
                : []
        },
        requestedEvidenceReportTypes() {
            return this.context.request && Array.isArray(this.context.request.evidenceReportTypes)
                ? this.context.request.evidenceReportTypes
                : []
        },
        providedVerificationDocuments() {
            const replyDocuments = this.context.replyDocuments

            return replyDocuments && Array.isArray(replyDocuments.verificationDocuments)
                ? replyDocuments.verificationDocuments
                : []
        },
        previewProduct() {
            return this.context.product || { id: this.context.productId }
        },
        reviewProfile() {
            if (this.selectedResearchProcessId) {
                return this.settings.researchReviewProfile
                    || 'received-request-guided-document-research-review'
            }

            return this.settings.reviewProfile || 'received-request-guided-document-review'
        },
        reviewContext() {
            const productLookup = {
                mode: this.settings.productLookupMode || 'organization-products',
                preferredProductId: this.context.productId
                    || (this.context.product && this.context.product.id)
                    || null,
            }
            const search = this.context.services && this.context.services.searchReviewProducts
            if (typeof search === 'function') {
                productLookup.search = search
            }

            return { productLookup }
        },
        acceptedExtensions() {
            return (this.settings.acceptedExtensions || [])
                .map(extension => extension.replace(/^\./, '').toLowerCase())
                .filter(Boolean)
        },
        acceptValue() {
            return this.acceptedExtensions.map(extension => `.${extension}`).join(',')
        },
        supportedFormatsLabel() {
            const formats = this.acceptedExtensions.map(extension => extension.toUpperCase()).join(', ')

            return this.translate(
                'Page(ReceivedRequest):Component(DocumentsAndWebpages):supported-formats',
                `Supported formats: ${formats}`,
                { formats },
            )
        },
        completedUploadRevision() {
            return this.context.documentsAndWebpages.completedUploadRevision
        },
        documents() {
            return this.context.documentsAndWebpages.documents || []
        },
        documentationLinks() {
            return this.context.documentsAndWebpages.links || []
        },
        researchProcesses() {
            return this.context.documentsAndWebpages.researchProcesses || []
        },
        researchValidationKey() {
            return this.researchProcesses
                .map(process => `${process.processId}:${process.status}`)
                .join('|')
        },
        hasBlockingResearch() {
            return this.researchProcesses.some(process => (
                blocksDocumentResearchCompletion(process.status)
            ))
        },
        hasResearchActionRequired() {
            return this.researchProcesses.some(process => (
                isActionRequiredDocumentResearchStatus(process.status)
            ))
        },
        hasResearchProcessing() {
            return this.researchProcesses.some(process => (
                isActiveDocumentResearchStatus(process.status)
            ))
        },
        canInlineCandidatePreview() {
            const contentType = this.candidatePreviewType.split(';')[0].trim().toLowerCase()
            return contentType === 'application/pdf'
                || contentType.startsWith('image/')
                || contentType.startsWith('text/')
        },
        reviewRefreshPendingProcessIds() {
            return this.context.documentsAndWebpages.reviewRefreshPendingProcessIds || []
        },
        hasDocumentsAwaitingReview() {
            return this.documents.some(document => (
                this.isReviewPending(document)
                && !this.isReviewRefreshPending(document)
            ))
        },
        hasDocumentsInAnalysis() {
            return this.documents.some(document => this.isAnalysisProcessing(document))
        },
        completedLinkRevision() {
            return this.context.documentsAndWebpages.completedLinkRevision
        },
        linkSavePending() {
            return this.pendingLinkRevision !== null
        },
    },
    watch: {
        completedUploadRevision(revision) {
            if (revision === this.pendingUploadRevision) {
                this.clearSelection()
                this.pendingUploadRevision = null
            }
        },
        completedLinkRevision(revision) {
            if (revision === this.pendingLinkRevision) {
                this.webpageUrl = ''
                this.webpageError = null
                this.pendingLinkRevision = null
                this.emitValidity()
            }
        },
        hasDocumentsAwaitingReview() {
            this.emitValidity()
        },
        hasDocumentsInAnalysis() {
            this.emitValidity()
        },
        researchValidationKey() {
            this.emitValidity()
        },
    },
    mounted() {
        this.emitValidity()
    },
    beforeDestroy() {
        this.cleanupCandidatePreview()
    },
    methods: {
        translate(key, fallback, params = {}) {
            const translation = this.$t(key, params)

            return translation && translation !== key ? translation : fallback
        },
        handleSelectedFiles(files) {
            let selected = []
            if (files) {
                selected = Array.isArray(files) ? files : [files]
            }
            const unsupported = selected.filter(file => !this.isAccepted(file))

            this.queuedFiles = selected
            this.selectionError = unsupported.length > 0
                ? this.translate(
                    'Page(ReceivedRequest):Component(DocumentsAndWebpages):unsupported-files',
                    'Some selected files have an unsupported format.',
                )
                : null
            this.emitValidity()
        },
        handleDragEnter() {
            this.dragDepth += 1
            this.isDragging = true
        },
        handleDragLeave() {
            this.dragDepth = Math.max(0, this.dragDepth - 1)
            this.isDragging = this.dragDepth > 0
        },
        handleDrop(event) {
            this.dragDepth = 0
            this.isDragging = false

            const droppedFiles = event.dataTransfer && event.dataTransfer.files
                ? Array.from(event.dataTransfer.files)
                : []
            const selected = this.settings.multipleFiles === false
                ? droppedFiles.slice(0, 1)
                : droppedFiles

            this.selectedFiles = selected
            this.handleSelectedFiles(selected)
        },
        openFilePicker() {
            const fileComponent = this.$refs.fileInput
            const input = fileComponent && fileComponent.$el
                ? fileComponent.$el.querySelector('input[type="file"]')
                : null

            if (input) {
                input.click()
            }
        },
        isAccepted(file) {
            const extension = file && file.name && file.name.includes('.')
                ? file.name.split('.').pop().toLowerCase()
                : ''

            return this.acceptedExtensions.includes(extension)
        },
        fileKey(file) {
            return `${file.name}-${file.size || 0}-${file.lastModified || 0}`
        },
        clearSelection() {
            this.selectedFiles = []
            this.queuedFiles = []
            this.selectionError = null
            this.emitValidity()
        },
        handleWebpageInput() {
            this.webpageError = null
            this.emitValidity()
        },
        normalizedWebpageUrl() {
            return this.webpageUrl.trim()
        },
        nextRevision(localRevision, completedRevision) {
            const completed = Number(completedRevision)
            const normalizedCompleted = Number.isFinite(completed) ? completed : 0

            return Math.max(localRevision, normalizedCompleted) + 1
        },
        validateWebpageUrl(value) {
            if (!value) {
                return this.translate(
                    'Page(ReceivedRequest):Component(DocumentsAndWebpages):url-required',
                    'Enter a URL.',
                )
            }

            try {
                const parsed = new URL(value)
                if (
                    !['http:', 'https:'].includes(parsed.protocol)
                    || parsed.username
                    || parsed.password
                ) {
                    throw new Error('Unsupported URL')
                }
            } catch (error) {
                return this.translate(
                    'Page(ReceivedRequest):Component(DocumentsAndWebpages):url-invalid',
                    'Enter an absolute HTTP or HTTPS URL.',
                )
            }

            return null
        },
        addWebpage() {
            if (this.runtimeState.isSaving) {
                return
            }

            const url = this.normalizedWebpageUrl()
            this.webpageError = this.validateWebpageUrl(url)
            if (this.webpageError) {
                this.emitValidity()
                return
            }

            this.linkRevision = this.nextRevision(
                this.linkRevision,
                this.completedLinkRevision,
            )
            this.pendingLinkRevision = this.linkRevision
            this.$emit('action', {
                type: 'documents-and-webpages.links.add',
                payload: {
                    urls: [url],
                    revision: this.pendingLinkRevision,
                    failureCallback: this.handleWebpageSaveFailure,
                },
            })
            this.emitValidity()
        },
        handleWebpageSaveFailure(error) {
            this.pendingLinkRevision = null
            const data = error && error.response ? error.response.data : null
            const violation = data && Array.isArray(data.violations) ? data.violations[0] : null
            this.webpageError = (violation && violation.message)
                || (data && (data.detail || data['hydra:description']))
                || (error && error.message)
                || this.translate(
                    'Page(ReceivedRequest):Component(DocumentsAndWebpages):url-save-failed',
                    'The URL could not be saved.',
                )
            this.emitValidity()
        },
        emitValidity() {
            const reviewErrors = this.hasDocumentsAwaitingReview ? [{
                code: 'DOCUMENT_REVIEW_REQUIRED',
                messageKey: 'Page(ReceivedRequest):Component(DocumentsAndWebpages):review-required-notice',
            }] : []
            const analysisErrors = this.hasDocumentsInAnalysis ? [{
                code: 'DOCUMENT_ANALYSIS_IN_PROGRESS',
                messageKey: 'Page(ReceivedRequest):Component(DocumentsAndWebpages):analysis-in-progress',
                message: 'Wait until document analysis is complete.',
            }] : []
            const researchActionErrors = this.hasResearchActionRequired ? [{
                code: 'DOCUMENT_RESEARCH_ACTION_REQUIRED',
                messageKey: 'Page(ReceivedRequest):Component(DocumentsAndWebpages):research-action-required',
                message: 'Select discovered documents or finish without documents.',
            }] : []
            const researchProcessingErrors = this.hasResearchProcessing ? [{
                code: 'DOCUMENT_RESEARCH_IN_PROGRESS',
                messageKey: 'Page(ReceivedRequest):Component(DocumentsAndWebpages):research-in-progress',
                message: 'Wait until document research is complete.',
            }] : []

            this.$emit('validity-change', {
                isValid: this.queuedFiles.length === 0
                    && this.normalizedWebpageUrl() === ''
                    && !this.linkSavePending
                    && !this.hasDocumentsInAnalysis
                    && !this.hasDocumentsAwaitingReview
                    && !this.hasBlockingResearch,
                errors: [
                    ...analysisErrors,
                    ...reviewErrors,
                    ...researchActionErrors,
                    ...researchProcessingErrors,
                ],
            })
        },
        researchProcessFor(link) {
            const processId = link && link.documentResearch
                ? link.documentResearch.processId
                : null

            return this.researchProcesses.find(process => (
                String(process.processId) === String(processId)
            )) || null
        },
        previewResearchCandidate({ processId, candidate }) {
            this.cleanupCandidatePreview()
            const revision = this.candidatePreviewRevision + 1
            this.candidatePreviewRevision = revision
            this.previewCandidate = candidate
            this.previewingCandidateId = candidate.id
            this.candidatePreviewVisible = true
            this.candidatePreviewLoading = true
            this.$emit('action', {
                type: 'documents-and-webpages.research.preview',
                payload: {
                    processId,
                    candidateId: candidate.id,
                    successCallback: response => {
                        if (revision === this.candidatePreviewRevision) {
                            this.applyCandidatePreview(response, candidate)
                        }
                    },
                    failureCallback: error => {
                        if (revision === this.candidatePreviewRevision) {
                            this.handleCandidatePreviewFailure(error)
                        }
                    },
                },
            })
        },
        applyCandidatePreview(response, candidate) {
            const responseType = response && response.headers
                ? response.headers['content-type']
                : null
            const contentType = responseType || candidate.contentType || 'application/octet-stream'
            const data = response ? response.data : null
            const blob = data instanceof Blob ? data : new Blob([data], { type: contentType })

            this.candidatePreviewType = contentType
            this.candidatePreviewUrl = URL.createObjectURL(blob)
            this.candidatePreviewLoading = false
            this.previewingCandidateId = null
        },
        handleCandidatePreviewFailure(error) {
            const data = error && error.response ? error.response.data : null
            this.candidatePreviewError = (data && (data.detail || data['hydra:description']))
                || (error && error.message)
                || this.translate(
                    'Page(ReceivedRequest):Component(DocumentsAndWebpages):preview-failed',
                    'The document preview could not be loaded.',
                )
            this.candidatePreviewLoading = false
            this.previewingCandidateId = null
        },
        cleanupCandidatePreview() {
            this.candidatePreviewRevision += 1
            if (this.candidatePreviewUrl) {
                URL.revokeObjectURL(this.candidatePreviewUrl)
            }
            this.candidatePreviewUrl = ''
            this.candidatePreviewType = ''
            this.candidatePreviewError = null
            this.candidatePreviewLoading = false
            this.previewingCandidateId = null
            this.previewCandidate = null
        },
        selectResearchCandidates(payload) {
            this.$emit('action', {
                type: 'documents-and-webpages.research.select',
                payload,
            })
        },
        finishResearchWithoutDocuments(payload) {
            this.$emit('action', {
                type: 'documents-and-webpages.research.finish-without-documents',
                payload,
            })
        },
        upload() {
            if (!this.queuedFiles.length || this.selectionError || this.runtimeState.isSaving) {
                return
            }

            this.uploadRevision = this.nextRevision(
                this.uploadRevision,
                this.completedUploadRevision,
            )
            this.pendingUploadRevision = this.uploadRevision
            this.$emit('action', {
                type: 'documents-and-webpages.upload',
                payload: {
                    files: [...this.queuedFiles],
                    revision: this.pendingUploadRevision,
                },
            })
        },
        documentName(document) {
            return document.file && document.file.name ? document.file.name : ''
        },
        processingStatus(document) {
            return getDocumentProcessingStatus(document)
        },
        reviewProcessId(document) {
            return getDocumentReviewProcessId(document)
        },
        isAnalysisProcessing(document) {
            return isDocumentAnalysisProcessing(document)
        },
        failureReason(document) {
            return document.processing && document.processing.failureReason
                ? document.processing.failureReason
                : null
        },
        statusVariant(document) {
            const variants = {
                [DocumentProcessingStatus.UPLOADED]: 'light-secondary',
                [DocumentProcessingStatus.CREATED]: 'light-secondary',
                [DocumentProcessingStatus.ANALYSIS_PENDING]: 'light-warning',
                [DocumentProcessingStatus.ANALYSIS_COMPLETED]: 'light-info',
                [DocumentProcessingStatus.REVIEW_PENDING]: 'light-primary',
                [DocumentProcessingStatus.COMPLETED]: 'light-success',
                [DocumentProcessingStatus.REJECTED]: 'light-danger',
                [DocumentProcessingStatus.FAILED]: 'light-danger',
            }

            return variants[this.processingStatus(document)] || 'light-secondary'
        },
        statusLabel(document) {
            const status = this.processingStatus(document)

            return this.translate(
                `Component(ProductDocumentProcessingStatusEnum):${status.replace(/_/g, '-')}`,
                STATUS_FALLBACKS[status] || status,
            )
        },
        isReviewPending(document) {
            return this.processingStatus(document) === DocumentProcessingStatus.REVIEW_PENDING
        },
        canPreviewDocument(document) {
            return Boolean(document && document.id && this.previewProduct.id)
                && this.processingStatus(document) === DocumentProcessingStatus.COMPLETED
        },
        openDocumentPreview(document) {
            if (!this.canPreviewDocument(document)) {
                return
            }

            this.previewDocument = document
            this.$nextTick(() => {
                this.$refs.documentPreview.show()
            })
        },
        isReviewRefreshPending(document) {
            const processId = document && document.processing
                ? document.processing.processId
                : null

            return processId !== null
                && typeof processId !== 'undefined'
                && this.reviewRefreshPendingProcessIds.map(String).includes(String(processId))
        },
        async openReview(document) {
            const processId = this.reviewProcessId(document)

            if (!processId || this.reviewTranslationsLoading) {
                return
            }

            this.reviewTranslationsLoading = true
            try {
                await loadTranslationGroups([DOCUMENT_REVIEW_TRANSLATION_GROUP])
                this.selectedReviewProcessId = processId
                this.selectedDocumentProcessId = document.processing
                    ? document.processing.processId
                    : null
                this.selectedResearchProcessId = null
                this.reviewVisible = true
            } catch (error) {
                this.$emit('error', error)
            } finally {
                this.reviewTranslationsLoading = false
            }
        },
        async openResearchReview(process) {
            if (!process || !process.reviewProcessId || this.reviewTranslationsLoading) {
                return
            }

            this.reviewTranslationsLoading = true
            try {
                await loadTranslationGroups([DOCUMENT_REVIEW_TRANSLATION_GROUP])
                this.selectedReviewProcessId = process.reviewProcessId
                this.selectedResearchProcessId = process.processId
                this.selectedDocumentProcessId = null
                this.reviewVisible = true
            } catch (error) {
                this.$emit('error', error)
            } finally {
                this.reviewTranslationsLoading = false
            }
        },
        closeReview() {
            this.reviewVisible = false
            this.selectedReviewProcessId = null
            this.selectedDocumentProcessId = null
            this.selectedResearchProcessId = null
        },
        handleReviewCompleted() {
            const documentProcessId = this.selectedDocumentProcessId
            const researchProcessId = this.selectedResearchProcessId

            this.closeReview()
            if (researchProcessId) {
                this.$emit('action', {
                    type: 'documents-and-webpages.research.review-completed',
                    payload: { processId: researchProcessId },
                })
                return
            }
            this.$emit('action', {
                type: 'documents-and-webpages.review-completed',
                payload: { processId: documentProcessId },
            })
        },
    },
}
</script>

<style lang="scss" scoped>
.documents-and-webpages-step__source-panel {
    max-width: 100%;
    padding: 1.5rem;
    border: 1px solid #ebe9f1;
    border-radius: .357rem;
}

.documents-and-webpages-step__drop-area {
    padding: 1.25rem;
    border: 1px dashed #d8d6de;
    border-radius: .357rem;
    background-color: #fff;
    transition: border-color .15s ease, background-color .15s ease;
}

.documents-and-webpages-step__drop-area--dragging {
    border-color: #f58049;
    background-color: rgba(245, 128, 73, .04);
}

.documents-and-webpages-step__drop-prompt {
    color: #2f2c52;
    cursor: pointer;

    &:focus-visible {
        outline: 2px solid rgba(245, 128, 73, .35);
        outline-offset: 2px;
    }
}

.documents-and-webpages-step__link-actions {
    display: flex;
    align-items: center;
    gap: 1rem;

    > .btn {
        flex: 0 0 auto;
    }
}

.documents-and-webpages-step__link-processing-note {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: .5rem;
    color: var(--workflow-secondary-text-color, #6e6b7b);

    svg {
        flex: 0 0 auto;
    }
}

.documents-and-webpages-step__source-row {
    display: flex;
    align-items: center;
    min-height: 4rem;
    padding: .85rem 1rem;
    border: 1px solid #ebe9f1;
    border-radius: .357rem;

    & + & {
        margin-top: .75rem;
    }
}

.documents-and-webpages-step__source-row + .document-research-source {
    margin-top: .75rem;
}

.documents-and-webpages-step__file-icon {
    display: inline-flex;
    flex: 0 0 2.5rem;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    margin-right: 1rem;
    border: 1px solid rgba(245, 128, 73, .55);
    border-radius: .357rem;
    background: transparent;
    color: #f58049;
}

.documents-and-webpages-step__source-copy {
    display: flex;
    min-width: 0;
    flex: 1 1 auto;
    flex-direction: column;
}

.documents-and-webpages-step__file-name {
    max-width: 100%;
    overflow: hidden;
    color: #2f2c52;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.documents-and-webpages-step__status {
    flex: 0 0 auto;
    margin: 0 1rem;
}

.documents-and-webpages-step__preview {
    flex: 0 0 auto;
}

.documents-and-webpages-step__candidate-preview {
    width: 100%;
    min-height: 70vh;
    border: 0;
}

@media (max-width: 575.98px) {
    .documents-and-webpages-step__link-actions {
        align-items: flex-start;
        flex-direction: column;
    }

    .documents-and-webpages-step__source-row {
        align-items: flex-start;
        flex-wrap: wrap;
    }

    .documents-and-webpages-step__status {
        margin: .75rem .75rem 0 3.5rem;
    }

    .documents-and-webpages-step__source-row .btn {
        margin-top: .5rem;
    }
}
</style>
